import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import moment from 'moment-timezone'
import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel, Grid, Typography } from '@mui/material'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import 'date-fns'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import DatePicker from '@mui/lab/DatePicker'
import { RootState } from '../../reducers'
import { fetchUserProfileData } from '../../actions'
import { IFormInputs } from 'interfaces/user'
import { useStyles } from './UserProfileStyles'

export default function UserView({ user }): JSX.Element {
  const classes = useStyles()
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(new Date())
  const dispatch = useDispatch()
  const { data } = useSelector((state: RootState) => state.getUserProfile)
  const [overridePermitted, setOverridePermitted] = useState(false)
  const [qcAllowed, setQcAllowed] = useState(false)
  const [priorityAllowed, setPriorityAllowed] = useState(false)
  const [mcAllowed, setMcAllowed] = useState(false)
  const [multipleClassifiersAllowed, setMultipleClassifiersAllowed] = useState(false)
  const [processCodes, setProcessCodes] = useState<string[]>([])
  const { procedure_codes, states } = useSelector((state: RootState) => state.appConfig)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const userProData = data ? data[0] : {}
  const isShow = user?.user_roles[0] === 'NL_Pathologist' ? true : false

  useEffect(() => {
    dispatch(fetchUserProfileData(user.user_id))
    setProcessCodes(procedure_codes)
  }, [dispatch, user.user_id])

  const { register, setValue } = useForm<IFormInputs>({
    mode: 'onChange',
  })

  useEffect(() => {
    if (userProData) {
      setValue('first_name', userProData.first_name)
      setValue('last_name', userProData.last_name)
      setValue('credential', userProData.credential)
      setValue('email_id', userProData.email_id)
      setValue('phone_number', userProData.phone_number)
      setValue('user_status', userProData.user_status)
      setValue('any_state_override', userProData.any_state_override)
      setValue('max_cases_per_day', userProData.max_cases_per_day)
      setValue('qc_allowed', userProData.qc_allowed)
      setValue('priority_allowed', userProData.priority_allowed)
      setValue('mc_allowed', userProData.mc_allowed)
      setValue('multiple_classifiers_allowed', userProData.multiple_classifiers_allowed)
      setValue('lab_os_user_code', user.lab_os_user_code)
      setValue('lab_os_login_id', user.lab_os_login_id)
      setOverridePermitted(userProData.any_state_override)
      setQcAllowed(userProData.qc_allowed)
      setPriorityAllowed(userProData.priority_allowed)
      setMcAllowed(userProData.mc_allowed)
      setMultipleClassifiersAllowed(userProData.multiple_classifiers_allowed)
    }
  }, [data, setValue, userProData])

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date)
    const dateStr = moment.tz(date, moment.locale()).format('YYYY-MM-DD').toString()
    setValue('expiry_date', dateStr)
  }

  const onCodeChange = (val) => {
    setValue('procedure_code_id', val)
  }

  const dataProcedurecode = (val) => {
    const filterProcedureCodes = procedure_codes.filter((value) => {
      return value.id == val
    })
    if (val == '') {
      return ''
    } else {
      return filterProcedureCodes[0]?.cdx_procedure_code + ' - ' + filterProcedureCodes[0]?.description
    }
  }

  const onStateChange = (val) => {
    setValue('state_code', val)
  }

  return (
    <React.Fragment>
      <form>
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={{ mt: '0px', mb: '5px', fontWeight: 600 }} variant="h6">
            Contact Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                inputProps={{
                  style: {
                    padding: 10,
                  },
                }}
                id="outlined-basic"
                label="First Name"
                {...register('first_name')}
                variant="outlined"
                disabled={true}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                inputProps={{
                  style: {
                    padding: 10,
                  },
                }}
                id="outlined-basic"
                label="Last Name"
                {...register('last_name')}
                variant="outlined"
                disabled={true}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                inputProps={{
                  style: {
                    padding: 10,
                  },
                }}
                id="outlined-basic"
                label="Credentials (ex. M.D.)"
                {...register('credential')}
                variant="outlined"
                disabled={true}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className={classes.mT10}>
            <Grid item xs={4}>
              <TextField
                inputProps={{
                  style: {
                    padding: 10,
                  },
                }}
                id="outlined-basic"
                label="Email Address"
                {...register('email_id')}
                variant="outlined"
                disabled={true}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                inputProps={{
                  style: {
                    padding: 10,
                  },
                }}
                id="outlined-basic"
                label="Phone"
                {...register('phone_number')}
                variant="outlined"
                disabled={true}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          {isShow && (
            <>
              <h3 className={classes.lableViewMrg}>State Licenses</h3>
              <Grid item xs={12} className={classes.mT20}>
                <TextField
                  inputProps={{
                    style: {
                      padding: 10,
                    },
                  }}
                  id="outlined-basic"
                  label="Account Status"
                  {...register('user_status')}
                  variant="outlined"
                  disabled={true}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              {userProData &&
                userProData?.user_licenses?.map((val, i) => (
                  // eslint-disable-next-line react/jsx-key
                  <Grid key={i} container spacing={2} className={classes.mT10}>
                    <Grid item xs={4}>
                      <Autocomplete
                        id="combo-box-demo"
                        value={val.state_code}
                        options={states}
                        style={{ marginLeft: 0, paddingLeft: 0 }}
                        onChange={(_e, val) => onStateChange(val)}
                        disabled={true}
                        renderInput={(params) => (
                          <TextField {...params} label="State/Locale" variant="outlined" size="small" />
                        )}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          value={val.expiry_date}
                          label="Expires"
                          disabled
                          onChange={handleDateChange}
                          InputProps={{
                            style: {
                              height: 40,
                            },
                          }}
                          renderInput={(params) => <TextField {...params} />}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                ))}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControlLabel
                    value={true}
                    control={<Checkbox color="primary" />}
                    {...register('any_state_override')}
                    label="User can perform state overides"
                    checked={overridePermitted}
                    labelPlacement="end"
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {isShow && (
            <>
              <h3 className={classes.lableViewMrg}>Caseload Settings</h3>
              <Grid container spacing={2} className={classes.mT10}>
                <Grid item xs={12}>
                  <TextField
                    inputProps={{
                      style: {
                        padding: 10,
                      },
                    }}
                    id="outlined-basic"
                    label="Max cases per day"
                    {...register('max_cases_per_day')}
                    variant="outlined"
                    disabled={true}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
              <FormControl component="fieldset" className={`${classes.fieldset}`}>
                <FormLabel style={{ fontSize: '15px', fontWeight: 'bold' }}>Procedure Codes</FormLabel>
                <FormGroup row>
                  {/*Your Input fields e.g TextField or Select e.t.c*/}
                  {userProData &&
                    userProData?.user_qc_settings?.map((val, i) => (
                      // eslint-disable-next-line react/jsx-key
                      <Grid key={i} container spacing={2} className={classes.mT10}>
                        <Grid item xs={6}>
                          <Autocomplete
                            id="combo-box-demo"
                            options={processCodes}
                            value={dataProcedurecode(val?.procedure_code_id)}
                            onChange={(e, val) => onCodeChange(val)}
                            disabled={true}
                            renderInput={(params) => (
                              <TextField {...params} label="Procedure Code" variant="outlined" size="small" />
                            )}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            inputProps={{
                              style: {
                                padding: 10,
                              },
                              min: 1,
                              max: 100,
                            }}
                            id="outlined-basic"
                            {...register('qc_allowed')}
                            variant="outlined"
                            value={val.qc_percentage}
                            label="QC allocation percentage"
                            disabled={true}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                      </Grid>
                    ))}
                </FormGroup>
              </FormControl>
            </>
          )}
          {isShow && (
            <>
              <Grid container spacing={2} className={classes.mp0}>
                <h3 className={classes.lableViewMrg}>Qualifications</h3>
                <Grid item xs={12} className={`${classes.mpTB0}`}>
                  <FormControlLabel
                    value={true}
                    control={<Checkbox color="primary" />}
                    {...register('qc_allowed')}
                    className={classes.dBlock}
                    label="User is qualified to perform QC."
                    labelPlacement="end"
                    checked={qcAllowed}
                    disabled={true}
                  />
                  <FormControlLabel
                    value={true}
                    control={<Checkbox color="primary" />}
                    {...register('priority_allowed')}
                    className={classes.dBlock}
                    label="User is qualified to accept Priority and/or Hand Hold cases."
                    labelPlacement="end"
                    checked={priorityAllowed}
                    disabled={true}
                  />
                  <FormControlLabel
                    value={true}
                    control={<Checkbox color="primary" />}
                    {...register('mc_allowed')}
                    className={classes.dBlock}
                    label="User is qualified to process Medicare cases"
                    labelPlacement="end"
                    checked={mcAllowed}
                    disabled={true}
                  />
                  <FormControlLabel
                    value={true}
                    control={<Checkbox color="primary" />}
                    {...register('multiple_classifiers_allowed')}
                    className={classes.dBlock}
                    label="User is qualified to view multiple classifiers"
                    labelPlacement="end"
                    checked={multipleClassifiersAllowed}
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </>
          )}
          {isShow && (
            <>
              <Grid container spacing={2} className={classes.mp0}>
                <Grid item xs={12} className={classes.mB10}>
                  <h3 className={classes.lableViewMrg}>LabOS login details</h3>
                </Grid>
                <Grid item xs={2} className={`${classes.mpTB0}, ${classes.mB80}`}>
                  <TextField
                    className={classes.customLableTxt}
                    style={{ width: '100%' }}
                    InputLabelProps={{
                      style: { border: '1px solid red !important' },
                      shrink: true,
                    }}
                    inputProps={{
                      style: {
                        padding: 10,
                      },
                      min: 1,
                    }}
                    id="outlined-basic"
                    label="User Code"
                    //type="number"
                    disabled={true}
                    {...register('lab_os_user_code')}
                  />
                </Grid>
                <Grid item xs={2} className={classes.mpTB0}>
                  <TextField
                    className={classes.customLableTxt}
                    style={{ width: '100%' }}
                    InputLabelProps={{
                      style: { border: '1px solid red !important' },
                      shrink: true,
                    }}
                    inputProps={{
                      style: {
                        padding: 10,
                      },
                    }}
                    id="outlined-basic"
                    label="Login ID"
                    type="text"
                    disabled={true}
                    {...register('lab_os_login_id')}
                  />
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      </form>
    </React.Fragment>
  )
}
